import { AlertMessage, useAlertConfirm } from "../../components/alerts";
import { useCallback, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheck,
  Plus,
  KeyRound,
  Trash2,
  PencilLine,
  X,
  TriangleAlert,
} from "lucide-react";
import type { AdminDto, AdminWithPasswordDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { Button } from "../../components/motion/button/base";
import { Input } from "../../components/motion/input";
import { Table, type TableColumn } from "../../components/motion/table";
import { Identicon } from "../../components/Identicon.js";
import { tableHeight } from "../../components/domain.js";
import {
  CenterMorphModal,
  CenterMorphModalTrigger,
  CenterMorphModalContent,
  CenterMorphModalClose,
} from "../../components/motion/center-morph-modal";

const fields = {
  field: "rounded-xl bg-background",
  label: "px-0",
  input: "placeholder:text-muted-foreground",
};

function Feedback({ children, error = false }: { children: ReactNode; error?: boolean }) {
  return <AlertMessage tone={error ? "error" : "info"}>{children}</AlertMessage>;
}

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Não foi possível concluir a operação.";

export function AdminsPage(): React.JSX.Element {
  const confirmAlert = useAlertConfirm();

  const queryClient = useQueryClient();
  const [credential, setCredential] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [editing, setEditing] = useState<AdminDto | null>(null);
  const credentialPanel = useRef<HTMLDivElement>(null);

  const admins = useQuery({
    queryKey: ["admin-accounts"],
    queryFn: () => api.get<AdminDto[]>("/admin/admins"),
  });

  const reload = () => queryClient.invalidateQueries({ queryKey: ["admin-accounts"] });

  const showCredential = (username: string, password: string) => {
    setCredential({ username, password });
    requestAnimationFrame(() => {
      credentialPanel.current?.focus();
      credentialPanel.current?.scrollIntoView({ behavior: "instant", block: "nearest" });
    });
  };

  const resetPassword = useMutation({
    mutationFn: (id: string) =>
      api.patch<{ admin: AdminDto; password: string }>(`/admin/admins/${id}`, {
        resetPassword: true,
      }),
    onSuccess: (r) => showCredential(r.admin.username, r.password),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/admins/${id}`),
    onSuccess: () => {
      setCredential(null);
      void reload();
    },
  });

  const list = admins.data ?? [];
  const busy = resetPassword.isPending || remove.isPending;
  const actionError = resetPassword.error ?? remove.error;

  const columns: TableColumn<AdminDto>[] = [
    {
      key: "username",
      header: "Usuário",
      cell: (admin) => (
        <div className="flex min-w-0 items-center gap-3">
          <Identicon
            seed={admin.id}
            className="size-8 border border-border"
            title={`Simbolo de ${admin.username}`}
          />
          <span className="truncate font-medium" title={admin.username}>
            {admin.username}
          </span>
          {admin.isSelf && (
            <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
              você
            </span>
          )}
        </div>
      ),
    },
    {
      key: "createdAt",
      header: "Criado em",
      width: "150px",
      cell: (admin) => (
        <span className="text-muted-foreground">
          {new Date(admin.createdAt).toLocaleDateString("pt-BR")}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Ações",
      width: "190px",
      align: "right",
      cell: (admin) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => {
              setEditing(admin);
              resetPassword.reset();
              remove.reset();
            }}
            aria-label={`Editar ${admin.username}`}
          >
            <PencilLine size={15} aria-hidden />
            Editar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={async () => {
              if (
                await confirmAlert(
                  `Gerar uma nova senha para "${admin.username}"? A senha atual deixa de funcionar.`,
                )
              ) {
                resetPassword.mutate(admin.id);
              }
            }}
            aria-label={`Nova senha para ${admin.username}`}
          >
            <KeyRound size={15} aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            // A própria conta nunca pode ser apagada daqui: quem clicasse
            // perderia o acesso na hora, possivelmente no meio da prova.
            disabled={busy || admin.isSelf || list.length <= 1}
            title={
              admin.isSelf
                ? "Você não pode apagar a própria conta"
                : list.length <= 1
                  ? "É o único administrador"
                  : undefined
            }
            onClick={async () => {
              if (
                await confirmAlert(
                  `Apagar o administrador "${admin.username}"? Ele perde o acesso imediatamente.`,
                )
              ) {
                remove.mutate(admin.id);
              }
            }}
            aria-label={`Apagar ${admin.username}`}
          >
            <Trash2 size={15} className="text-destructive" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-semibold tracking-tight">Administradores</h1>
          <p className="mt-1 max-w-xl text-sm leading-6 text-muted-foreground">
            Contas com acesso total: controlam a competição e enxergam o código-fonte
            enviado por todas as equipes.
          </p>
        </div>
        <CreatePanel
          credentialOpen={!!credential}
          onCreated={(username, password) => {
            void reload();
            showCredential(username, password);
          }}
        />
      </div>

      {credential && (
        <div
          ref={credentialPanel}
          tabIndex={-1}
          role="status"
          className="rounded-2xl border border-accent/40 bg-accent/5 p-5 outline-none"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="flex items-center gap-2 font-semibold">
                <TriangleAlert size={17} className="text-accent" aria-hidden />
                Anote a senha agora
              </h2>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                Ela aparece uma única vez. Depois disso só é possível gerar outra.
              </p>
              <p className="mt-4 select-all font-mono text-xl font-semibold tracking-wider">
                {credential.username} / {credential.password}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Fechar"
              onClick={() => setCredential(null)}
            >
              <X size={17} aria-hidden />
            </Button>
          </div>
        </div>
      )}

      {actionError && <Feedback error>{errorMessage(actionError)}</Feedback>}

      <div className="space-y-6">
        <section
          className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card"
          aria-label="Lista de administradores"
        >
          <header className="flex items-center gap-2 border-b border-border px-5 py-4">
            <ShieldCheck size={17} className="text-muted-foreground" aria-hidden />
            <h2 className="text-sm font-medium">
              {list.length} {list.length === 1 ? "administrador" : "administradores"}
            </h2>
          </header>

          {admins.error ? (
            <div className="space-y-3 p-5">
              <Feedback error>{errorMessage(admins.error)}</Feedback>
              <Button variant="secondary" onClick={() => void admins.refetch()}>
                Tentar novamente
              </Button>
            </div>
          ) : (
            <Table
              data={list}
              columns={columns}
              getRowId={(admin) => admin.id}
              loading={admins.isPending}
              rowHeight={64}
              height={tableHeight(list.length)}
              className="rounded-none border-0 bg-transparent"
              emptyState={
                <div className="space-y-2">
                  <ShieldCheck size={24} className="mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Nenhum administrador cadastrado.
                  </p>
                </div>
              }
            />
          )}
        </section>

        {editing && (
          <EditPanel
            admin={editing}
            onClose={() => setEditing(null)}
            onSaved={(username, password) => {
              setEditing(null);
              void reload();
              if (password) showCredential(username, password);
            }}
          />
        )}
      </div>
    </div>
  );
}

function CreatePanel({
  onCreated,
  credentialOpen,
}: {
  onCreated: (username: string, password: string) => void;
  credentialOpen: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api.post<AdminWithPasswordDto>("/admin/admins", {
        username: username.trim(),
        ...(password ? { password } : {}),
      }),
    onSuccess: (r) => {
      setOpen(false);
      setUsername("");
      setPassword("");
      onCreated(r.admin.username, r.password);
    },
  });

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!create.isPending) setOpen(next);
    },
    [create.isPending],
  );

  return (
    <CenterMorphModal open={open} onOpenChange={onOpenChange}>
      <CenterMorphModalTrigger>
        <Button
          className="rounded-xl"
          disabled={credentialOpen}
          title={
            credentialOpen
              ? "Anote e feche as credenciais antes de criar outro administrador."
              : undefined
          }
        >
          <Plus size={16} aria-hidden />
          Novo administrador
        </Button>
      </CenterMorphModalTrigger>
      <CenterMorphModalContent
        ariaLabel="Novo administrador"
        ariaDescribedBy="create-admin-description"
        closeButtonLabel="Fechar cadastro"
        dismissible={!create.isPending}
        showCloseButton={!create.isPending}
        className="admin-shell max-w-lg max-h-[calc(100dvh-4rem)] overflow-y-auto bg-card"
        backdropClassName="bg-background/70"
      >
        <form
          className="p-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (!create.isPending && !credentialOpen) create.mutate();
          }}
        >
          <h2 className="flex items-center gap-2 font-semibold">
            <Plus size={17} className="text-muted-foreground" aria-hidden />
            Novo administrador
          </h2>
          <p
            id="create-admin-description"
            className="mt-1 text-sm leading-6 text-muted-foreground"
          >
            Deixe a senha em branco para gerar uma aleatória e forte.
          </p>

          <div className="mt-5 space-y-4">
            <Input
              id="novo-admin-usuario"
              label="Usuário"
              placeholder="juiz-ana"
              value={username}
              onChange={setUsername}
              autoCapitalize="none"
              required
              minLength={3}
              maxLength={32}
              autoComplete="off"
              spellCheck={false}
              disabled={create.isPending}
              classNames={fields}
            />
            <Input
              id="novo-admin-senha"
              label="Senha (opcional)"
              placeholder="gerar automaticamente"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              minLength={8}
              maxLength={128}
              disabled={create.isPending}
              classNames={fields}
            />

            {create.error && <Feedback error>{errorMessage(create.error)}</Feedback>}

            <div className="flex justify-end gap-2 border-t border-border pt-5">
              <CenterMorphModalClose>
                <Button
                  variant="secondary"
                  className="rounded-xl"
                  disabled={create.isPending}
                >
                  Cancelar
                </Button>
              </CenterMorphModalClose>
              <Button
                type="submit"
                className="rounded-xl"
                disabled={create.isPending || username.trim().length < 3}
              >
                {create.isPending ? "Criando…" : "Criar administrador"}
              </Button>
            </div>
          </div>
        </form>
      </CenterMorphModalContent>
    </CenterMorphModal>
  );
}

function EditPanel({
  admin,
  onClose,
  onSaved,
}: {
  admin: AdminDto;
  onClose: () => void;
  onSaved: (username: string, password: string | null) => void;
}) {
  const [username, setUsername] = useState(admin.username);
  const [password, setPassword] = useState("");

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ admin: AdminDto; password: string | null }>(
        `/admin/admins/${admin.id}`,
        {
          ...(username.trim() !== admin.username ? { username: username.trim() } : {}),
          ...(password ? { password } : {}),
        },
      ),
    onSuccess: (r) => onSaved(r.admin.username, r.password),
  });

  const changed = username.trim() !== admin.username || password.length > 0;

  return (
    <section className="h-fit rounded-2xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-2">
        <h2 className="flex items-center gap-2 font-semibold">
          <PencilLine size={17} className="text-muted-foreground" aria-hidden />
          Editar {admin.username}
        </h2>
        <Button variant="ghost" size="icon" aria-label="Cancelar" onClick={onClose}>
          <X size={17} aria-hidden />
        </Button>
      </div>

      <div className="mt-5 space-y-4">
        <Input
          id="editar-admin-usuario"
          label="Usuário"
          value={username}
          onChange={setUsername}
          autoCapitalize="none"
          spellCheck={false}
          disabled={save.isPending}
          classNames={fields}
        />
        <Input
          id="editar-admin-senha"
          label="Nova senha (opcional)"
          placeholder="manter a atual"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          disabled={save.isPending}
          classNames={fields}
        />

        {admin.isSelf && changed && (
          <Feedback>
            Você está editando a própria conta. Sessões abertas continuam valendo até
            expirar, mas o próximo login usará os dados novos.
          </Feedback>
        )}

        {save.error && <Feedback error>{errorMessage(save.error)}</Feedback>}

        <div className="flex gap-2">
          <Button
            className="flex-1 rounded-xl"
            disabled={save.isPending || !changed || username.trim().length < 3}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Salvando…" : "Salvar"}
          </Button>
          <Button variant="secondary" className="rounded-xl" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
    </section>
  );
}
